import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

/* ------------------------------------------------------------------ */
/* Masked line-by-line reveal (hero signature moment).                */
/* Splits `text` into words, each wrapped in an overflow-hidden mask, */
/* and reveals them from below with a stagger.                        */
/* ------------------------------------------------------------------ */
export const MaskedText = ({
  text,
  as = "span",
  className = "",
  wordClassName = "",
  delay = 0,
  stagger = 0.06,
  duration = 0.9,
  once = true,
  testid,
}) => {
  const Tag = motion[as] || motion.span;
  const words = String(text).split(" ");
  return (
    <Tag
      className={className}
      initial="hidden"
      animate={once ? "show" : undefined}
      whileInView={once ? undefined : "show"}
      viewport={once ? undefined : { once: true, margin: "-10% 0px" }}
      data-testid={testid}
    >
      {words.map((w, i) => (
        <span
          key={`${w}-${i}`}
          className="inline-block overflow-hidden align-baseline"
          style={{ paddingBottom: "0.12em", marginBottom: "-0.12em" }}
        >
          <motion.span
            className={`inline-block will-change-transform ${wordClassName}`}
            variants={{
              hidden: { y: "110%", opacity: 0 },
              show: {
                y: 0,
                opacity: 1,
                transition: {
                  duration,
                  ease: [0.19, 1, 0.22, 1],
                  delay: delay + i * stagger,
                },
              },
            }}
          >
            {w}
            {i < words.length - 1 ? "\u00A0" : ""}
          </motion.span>
        </span>
      ))}
    </Tag>
  );
};

/* ------------------------------------------------------------------ */
/* Scroll-into-view reveal wrapper. Fades + slides up once in view.   */
/* ------------------------------------------------------------------ */
export const Reveal = ({
  children,
  delay = 0,
  y = 32,
  duration = 0.9,
  className = "",
  as: Tag = "div",
  once = true,
  margin = "-12% 0px",
}) => {
  const MotionTag = motion[Tag] || motion.div;
  const ref = useRef(null);
  const inView = useInView(ref, { once, margin });
  return (
    <MotionTag
      ref={ref}
      className={className}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y }}
      transition={{ duration, ease: [0.19, 1, 0.22, 1], delay }}
    >
      {children}
    </MotionTag>
  );
};

/* ------------------------------------------------------------------ */
/* Parallax image — subtle y-shift as it passes through the viewport. */
/* ------------------------------------------------------------------ */
export const ParallaxImage = ({
  src,
  alt,
  className = "",
  wrapperClassName = "",
  strength = 60,
  testid,
}) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [-strength, strength]);
  return (
    <div ref={ref} className={`relative overflow-hidden ${wrapperClassName}`} data-testid={testid}>
      <motion.img
        src={src}
        alt={alt}
        style={{ y }}
        className={`w-full h-full object-cover will-change-transform ${className}`}
        loading="lazy"
      />
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Clip-path image reveal — mask expands as the element enters view.  */
/* ------------------------------------------------------------------ */
export const ClipReveal = ({ src, alt, className = "", wrapperClassName = "", delay = 0, testid }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-15% 0px" });
  return (
    <div ref={ref} className={`relative overflow-hidden ${wrapperClassName}`} data-testid={testid}>
      <motion.img
        src={src}
        alt={alt}
        initial={{ clipPath: "inset(100% 0 0 0)", scale: 1.15 }}
        animate={inView ? { clipPath: "inset(0% 0 0 0)", scale: 1 } : {}}
        transition={{ duration: 1.4, ease: [0.19, 1, 0.22, 1], delay }}
        className={`w-full h-full object-cover ${className}`}
        loading="lazy"
      />
    </div>
  );
};

export default Reveal;
